import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { OrganizationalRecordPerson } from "./OrganizationalRecordPerson";
import { OrganizationalPowerAnalysisDialog } from "./OrganizationalPowerAnalysisDialog";
import { FormattedMessage, useIntl } from "react-intl";
import { useApolloClient } from "@apollo/client/react";
import { Button } from "@/components/design/Button";
import { createDocumentResource } from "@/lib/document-resource";
import { openDocumentResource } from "@/stores/document-preview-store";
import { DocumentLibrary } from "@/components/documents/DocumentLibrary";
import { ServiceMenuPopover, type ServiceMenuGroup } from "@/components/save/ServiceMenuPopover";
import { SERVICE_INPUTS } from "@/pages/save/service/service-actions";
import type { DocumentItem } from "@/components/documents/types";
import { fileOrganizationalRecordDocument, type FiledOrganizationalRecordDocument } from "./organizational-record-filing";
import {
  DeleteOrganizationalRecordDocumentDocument, OrganizationalRecordDocumentContentDocument,
  OrganizationalRecordDocumentPreviewDocument, OrganizationalRecordDocumentsDocument, UpdateOrganizationalRecordDocumentDocument,
  type OrganizationalRecordDocumentsQuery,
} from "@/graphql/generated/operations";
import { ingestFiles } from "@/lib/file-ingest";
import { downloadBlob } from "@/lib/file-download";
import { base64ToBlob } from "@/lib/use-open-document";
import { MAX_DOCUMENT_UPLOAD_BYTES, PROJECT_DOCUMENT_ACCEPT } from "@/lib/upload-limits";
import { userErrorMessage } from "@/lib/user-error";
import { LocalizedError } from "@/lib/localized-error";
import { OrganizationalRecordItemKind } from "@/graphql/generated/operations";
import type { OrganizationalRecordNavigate } from "./OrganizationalRecordLinks";
import type { OrganizationalRecordSubjectKind } from "./organizational-record-subject";

type Document = OrganizationalRecordDocumentsQuery["organizationalRecordDocuments"]["nodes"][number];
const thumbnailTypes = new Set(["image/png", "image/jpeg", "image/gif", "image/webp"]);
// The profile's Service menu, for the person the record is about. For now only its power analysis card,
// described for the record's documents; the other services follow.
const powerCard = SERVICE_INPUTS.find(card => card.action === "power")!;
const services: readonly ServiceMenuGroup[] = [{ titleId: "service.page.inputTitle", cards: [{ ...powerCard, descId: "organizationalRecord.powerAnalysisDesc" }] }];
const powerOnly: ReadonlySet<string> = new Set(["power"]);

/** Organizational adapter: no personal Document queries, normalized cache or assistant context. */
// `serviceSlot`: where the page wants the Service menu (beside its tabs); without one it sits above the list.
export function OrganizationalRecordDocuments({ recordId, canWrite, canAnalyzePowers = false, personId, subjectKind = "person", onNavigate, embedded = false, serviceSlot = null }: {
  recordId: string; canWrite: boolean; canAnalyzePowers?: boolean; personId?: string; subjectKind?: OrganizationalRecordSubjectKind; onNavigate?: OrganizationalRecordNavigate; embedded?: boolean; serviceSlot?: HTMLElement | null;
}) {
  const intl = useIntl();
  const client = useApolloClient();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [writeError, setWriteError] = useState<string | null>(null);
  const revision = useRef(0);

  // Search/filter/count use the complete metadata set. Each request is bounded; thumbnails and
  // original bytes are fetched separately, only for the displayed preview cards or an explicit open.
  const reload = useCallback(async () => {
    const current = ++revision.current;
    setLoading(true); setListError(null);
    try {
      const items: Document[] = [];
      const seen = new Set<string>();
      let after: string | undefined;
      while (true) {
        const { data } = await client.query({ query: OrganizationalRecordDocumentsDocument,
          variables: { input: { recordId, first: 100, ...(after ? { after } : {}) } }, fetchPolicy: "no-cache" });
        if (current !== revision.current) return;
        if (!data) throw new Error("Missing organizationalRecord documents");
        const page = data.organizationalRecordDocuments;
        items.push(...page.nodes);
        if (!page.pageInfo.hasNextPage) break;
        const cursor = page.pageInfo.endCursor;
        if (!cursor || seen.has(cursor)) throw new Error("OrganizationalRecord document cursor did not advance");
        seen.add(cursor); after = cursor;
      }
      setDocuments(Array.from(new Map(items.map(item => [item.id, item])).values()));
    } catch (failure) {
      if (current !== revision.current) return;
      setDocuments([]); setListError(userErrorMessage(intl, failure));
      throw failure;
    } finally { if (current === revision.current) setLoading(false); }
  }, [client, recordId, intl]);
  const cancelLoads = useCallback(() => { revision.current++; }, []);
  useEffect(() => {
    let active = true;
    queueMicrotask(() => { if (active) void reload().catch(() => undefined); });
    return () => { active = false; cancelLoads(); };
  }, [reload, cancelLoads]);

  const loadPreview = useCallback(async (id: string) => {
    const { data } = await client.query({ query: OrganizationalRecordDocumentPreviewDocument, variables: { id }, fetchPolicy: "no-cache" });
    const file = data?.organizationalRecordDocument?.preview;
    return file?.content && file.mimeType && thumbnailTypes.has(file.mimeType)
      ? `data:${file.mimeType};base64,${file.content}` : null;
  }, [client]);

  async function open(id: string, download = false) {
    const document = documents.find(item => item.id === id);
    if (!document) return;
    const { data } = await client.query({ query: OrganizationalRecordDocumentContentDocument, variables: { id }, fetchPolicy: "no-cache" });
    const file = data?.organizationalRecordDocument?.content;
    if (!file?.content) throw new LocalizedError(intl.formatMessage({ id: "service.page.errNoContent" }));
    const mime = file.mimeType ?? "application/octet-stream";
    const blob = base64ToBlob(file.content, mime);
    if (download) downloadBlob(blob, document.fileName);
    else await openDocumentResource(createDocumentResource({ name: document.fileName, mimeType: mime, load: async () => blob }));
  }

  async function upload(files: FileList | File[]) {
    if (!canWrite) throw new LocalizedError(intl.formatMessage({ id: "organizationalRecord.noAccess" }));
    setWriteError(null);
    const result = await ingestFiles(Array.from(files), { maxBytes: MAX_DOCUMENT_UPLOAD_BYTES, accept: PROJECT_DOCUMENT_ACCEPT });
    if (result.rejected.length || result.accepted.some(file => file.size === 0)) {
      throw new LocalizedError(intl.formatMessage({ id: "organizationalRecord.uploadRejected" }));
    }
    const added: FiledOrganizationalRecordDocument[] = [];
    try {
      for (const file of result.accepted) added.push(await fileOrganizationalRecordDocument(client, recordId, file));
      return added;
    } finally { await reload(); }
  }

  async function rename(id: string, title: string) {
    if (!canWrite) throw new LocalizedError(intl.formatMessage({ id: "organizationalRecord.noAccess" }));
    setWriteError(null);
    const { data } = await client.mutate({ mutation: UpdateOrganizationalRecordDocumentDocument, variables: { id, title }, fetchPolicy: "no-cache" });
    if (!data) throw new Error("Document rename was not confirmed");
    setDocuments(previous => previous.map(item => item.id === id ? { ...item, title: data.updateOrganizationalRecordDocument.title } : item));
  }

  async function remove(ids: readonly string[]) {
    if (!canWrite) throw new LocalizedError(intl.formatMessage({ id: "organizationalRecord.noAccess" }));
    const failed: string[] = [];
    setWriteError(null);
    for (const id of ids) {
      try {
        await client.mutate({ mutation: DeleteOrganizationalRecordDocumentDocument, variables: { id }, fetchPolicy: "no-cache" });
        setDocuments(previous => previous.filter(item => item.id !== id));
      } catch (failure) { failed.push(id); setWriteError(userErrorMessage(intl, failure)); }
    }
    return failed;
  }

  const items = useMemo<DocumentItem[]>(() => documents.map(document => {
    const kind = document.fileName.match(/\.([a-z0-9]{2,8})$/i)?.[1]?.toUpperCase() ?? "DOC";
    return { id: document.id, document: createDocumentResource({ name: document.fileName, loadThumbnail: () => loadPreview(document.id), load: async () => {
      const { data } = await client.query({ query: OrganizationalRecordDocumentContentDocument, variables: { id: document.id }, fetchPolicy: "no-cache" });
      const file = data?.organizationalRecordDocument?.content;
      if (!file?.content) throw new Error("Missing document content");
      return base64ToBlob(file.content, file.mimeType ?? "application/octet-stream");
    } }), name: document.title, kind, fileType: kind, when: document.createdAt,
      source: document.createdBy?.formattedName ?? "–", sourceDisplay: document.createdBy ? <OrganizationalRecordPerson person={document.createdBy} /> : undefined,
      groupId: null, preview: null, canDownload: true };
  }), [documents, client, loadPreview]);
  const service = canAnalyzePowers && personId ? <ServiceMenuPopover id="organizational-service-open" cardIdPrefix="organizational-service-card" groups={services}
    disabledActions={loading || listError || (!canWrite && !documents.length) ? powerOnly : undefined}
    // A card that cannot be used says why, instead of only greying out.
    note={!loading && !listError && !canWrite && !documents.length ? <span className="text-ink"><FormattedMessage id="organizationalRecord.powerAnalysisNeedsDocument" /></span> : null}
    onAction={() => setAnalysisOpen(true)} /> : null;
  return <>
    {service && (serviceSlot ? createPortal(service, serviceSlot) : <div className="mb-4 flex justify-end">{service}</div>)}
    {canAnalyzePowers && personId && <OrganizationalPowerAnalysisDialog recordId={recordId} personId={personId} open={analysisOpen}
      documents={documents} onUpload={canWrite ? upload : undefined} onClose={() => setAnalysisOpen(false)} />}
    <DocumentLibrary idPrefix="organizational-record-documents" purpose={onNavigate ? "inspect" : "browse"} scopeId={`organizationalRecord-documents:${recordId}`} items={items} embedded={embedded}
      title={<FormattedMessage id="organizationalRecord.documents" />} description={<FormattedMessage id="organizationalRecord.documentsHint" values={{ subject: subjectKind }} />}
      loading={loading} accept={PROJECT_DOCUMENT_ACCEPT} loadPreview={loadPreview}
      onOpen={id => onNavigate ? onNavigate({ id, kind: OrganizationalRecordItemKind.Document, title: documents.find(item => item.id === id)?.title ?? "" }) : open(id)} onDownload={id => open(id, true)}
      onUpload={canWrite ? async files => { await upload(files); } : undefined} onRename={canWrite ? rename : undefined} onDelete={canWrite ? remove : undefined}
      error={listError || writeError ? <>
        {(listError || writeError) && <p role="alert" className="mt-3 smycles-field-meta text-ink">{listError ?? writeError}</p>}
        {listError && <Button variant="secondary" size="sm" className="mt-2" onClick={() => void reload().catch(() => undefined)}>
          <FormattedMessage id="common.tryAgain" defaultMessage="Try again" /></Button>}
      </> : null}
    />

  </>;
}
